import { useState, useMemo, useEffect } from "react";

/**
 * OrderView - Full page view showing all jobs for a selected order
 * Displays: Header with order info, progress bar, job table with status/routing, quick actions
 */
export default function OrderView({
  order,
  quote,
  jobs = [],
  activeSessions = {},
  selectedOperation,
  onBack,
  onJobClick,
  onStartJob,
  onPauseJob,
  onResumeJob,
  onCompleteJob,
  onOperationSelect,
}) {
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortBy, setSortBy] = useState("jobNumber");
  const [sortDir, setSortDir] = useState("asc");
  const [jobRoutings, setJobRoutings] = useState({});
  const [loadingRoutings, setLoadingRoutings] = useState(false);

  // Fetch routing for all jobs to show operation progress
  useEffect(() => {
    if (!jobs.length) return;

    const controller = new AbortController();

    async function fetchAllRoutings() {
      setLoadingRoutings(true);
      const token = sessionStorage.getItem("msal.backendToken");
      if (!token) {
        setLoadingRoutings(false);
        return;
      }

      const routings = {};

      // Fetch routing for each job (in parallel, limited batch)
      const batchSize = 5;
      for (let i = 0; i < jobs.length; i += batchSize) {
        const batch = jobs.slice(i, i + batchSize);
        await Promise.all(
          batch.map(async (job) => {
            try {
              const res = await fetch(
                `/api/jobs/${job.jobNumber}/routing`,
                {
                  signal: controller.signal,
                  headers: { Authorization: `Bearer ${token}` },
                }
              );
              if (res.ok) {
                const data = await res.json();
                routings[job.id] = data.routing || [];
              }
            } catch (err) {
              if (err.name !== "AbortError") {
                console.error(`Error fetching routing for job ${job.jobNumber}:`, err);
              }
            }
          })
        );
      }

      setJobRoutings(routings);
      setLoadingRoutings(false);
    }

    fetchAllRoutings();
    return () => controller.abort();
  }, [jobs]);

  // Calculate job statistics
  const jobStats = useMemo(() => {
    const total = jobs.length;
    const withSessions = jobs.filter((j) => activeSessions[j.id]).length;
    const running = jobs.filter((j) => activeSessions[j.id]?.state === "running").length;
    const paused = jobs.filter((j) => activeSessions[j.id]?.state === "paused").length;
    const completed = jobs.filter((j) => j.status?.toLowerCase() === "closed").length;

    return { total, withSessions, running, paused, completed };
  }, [jobs, activeSessions]);

  // Filter jobs
  const filteredJobs = useMemo(() => {
    return jobs.filter((job) => {
      if (filterStatus === "all") return true;
      if (filterStatus === "active") return activeSessions[job.id]?.state === "running";
      if (filterStatus === "paused") return activeSessions[job.id]?.state === "paused";
      if (filterStatus === "pending") return !activeSessions[job.id] && job.status !== "Closed";
      if (filterStatus === "completed") return job.status?.toLowerCase() === "closed";
      return true;
    });
  }, [jobs, activeSessions, filterStatus]);

  // Sort jobs
  const sortedJobs = useMemo(() => {
    return [...filteredJobs].sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case "jobNumber":
          comparison = (a.jobNumber || "").localeCompare(b.jobNumber || "", undefined, { numeric: true });
          break;
        case "name":
          comparison = (a.name || "").localeCompare(b.name || "");
          break;
        case "qty":
          comparison = (a.qty || 0) - (b.qty || 0);
          break;
        case "status":
          const statusA = activeSessions[a.id]?.state || a.status || "pending";
          const statusB = activeSessions[b.id]?.state || b.status || "pending";
          comparison = statusA.localeCompare(statusB);
          break;
        default:
          break;
      }

      return sortDir === "asc" ? comparison : -comparison;
    });
  }, [filteredJobs, sortBy, sortDir, activeSessions]);

  // Format date
  function formatDate(dateStr) {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  }

  // Check if due date is past
  function isDueDatePast(dateStr) {
    if (!dateStr) return false;
    try {
      const dueDate = new Date(dateStr);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return dueDate < today;
    } catch {
      return false;
    }
  }

  // Get job status display
  function getJobStatusBadge(job) {
    const session = activeSessions[job.id];

    if (session?.state === "running") {
      return { text: "Running", color: "bg-green-500 text-gray-900", pulse: true };
    }
    if (session?.state === "paused") {
      return { text: "Paused", color: "bg-yellow-500 text-gray-900", pulse: false };
    }
    if (job.status?.toLowerCase() === "closed") {
      return { text: "Completed", color: "bg-slate-200 text-gray-900", pulse: false };
    }
    return { text: "Pending", color: "bg-gray-200 text-gray-700", pulse: false };
  }

  // Get operation progress for a job
  function getOperationProgress(job) {
    const routing = jobRoutings[job.id] || [];
    if (!routing.length) return null;

    const completed = routing.filter((step) => step.status === "C" || step.completedQty > 0).length;
    return { completed, total: routing.length };
  }

  // Handle sort
  function handleSort(column) {
    if (sortBy === column) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortDir("asc");
    }
  }

  // Sort indicator
  function SortIndicator({ column }) {
    if (sortBy !== column) return null;
    return <span className="ml-1">{sortDir === "asc" ? "↑" : "↓"}</span>;
  }

  if (!order) {
    return null;
  }

  const isPastDue = isDueDatePast(order.dueDate);
  const progressPercent = jobStats.total > 0
    ? Math.round((jobStats.completed / jobStats.total) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-slate-100 text-gray-900 flex flex-col">
      {/* HEADER */}
      <header className="w-full bg-gradient-to-r from-purple-600 via-pink-500 to-blue-500 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-gray-900/80 hover:text-gray-900 transition"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>
            <div className="h-6 w-px bg-slate-200" />
            <h1 className="text-xl font-semibold tracking-wide">Order View</h1>
          </div>
        </div>
      </header>

      {/* ORDER INFO CARD */}
      <div className="max-w-7xl mx-auto w-full px-4 py-4">
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          {/* Top Row - Quote & Order Numbers */}
          <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
            <div className="flex flex-wrap gap-6">
              <div>
                <span className="text-xs text-gray-500 uppercase">Quote</span>
                <div className="text-xl font-bold text-purple-600">
                  #{quote?.quoteNumber || order.quoteNumber || "—"}
                </div>
              </div>
              <div>
                <span className="text-xs text-gray-500 uppercase">Order</span>
                <div className="text-xl font-bold text-gray-900">
                  #{order.orderNumber}
                </div>
              </div>
              <div>
                <span className="text-xs text-gray-500 uppercase">Customer</span>
                <div className="text-lg font-semibold text-gray-800">
                  {order.customerCode || order.customer || "—"}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-6 text-right">
              <div>
                <span className="text-xs text-gray-500 uppercase">Project Manager</span>
                <div className="text-sm font-medium text-gray-800">
                  {order.projectManager || order.user_Text1 || "—"}
                </div>
              </div>
              <div>
                <span className="text-xs text-gray-500 uppercase">Due Date</span>
                <div className={`text-sm font-medium ${isPastDue ? "text-red-400" : "text-gray-800"}`}>
                  {isPastDue && "⚠️ "}
                  {formatDate(order.dueDate)}
                  {isPastDue && " (OVERDUE)"}
                </div>
              </div>
            </div>
          </div>

          {/* Project Name */}
          {(order.projectName || quote?.projectName) && (
            <div className="mb-4">
              <span className="text-xs text-gray-500 uppercase">Project</span>
              <div className="text-base text-gray-800">
                {order.projectName || quote?.projectName}
              </div>
            </div>
          )}

          {/* Progress Bar */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-gray-500">Job Progress</span>
              <span className="text-sm font-medium text-gray-800">
                {jobStats.completed} of {jobStats.total} jobs completed ({progressPercent}%)
              </span>
            </div>
            <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-green-500 to-green-400 transition-all duration-500"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          {/* Stats Row */}
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-lg">
              <span className="text-gray-500 text-sm">Total Jobs:</span>
              <span className="font-bold text-gray-900">{jobStats.total}</span>
            </div>
            {jobStats.running > 0 && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-green-600/20 border border-green-500/30 rounded-lg">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                <span className="text-green-600 text-sm">{jobStats.running} Running</span>
              </div>
            )}
            {jobStats.paused > 0 && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-yellow-600/20 border border-yellow-500/30 rounded-lg">
                <span className="text-yellow-600 text-sm">{jobStats.paused} Paused</span>
              </div>
            )}
            <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-lg">
              <span className="text-gray-500 text-sm">Completed:</span>
              <span className="font-bold text-green-400">{jobStats.completed}</span>
            </div>
          </div>
        </div>

        {/* FILTERS & SORT */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <div className="flex flex-wrap gap-2">
            <span className="text-sm text-gray-500 self-center mr-2">Filter:</span>
            {[
              { value: "all", label: "All" },
              { value: "pending", label: "Pending" },
              { value: "active", label: "Running" },
              { value: "paused", label: "Paused" },
              { value: "completed", label: "Completed" },
            ].map((option) => (
              <button
                key={option.value}
                onClick={() => setFilterStatus(option.value)}
                className={`px-3 py-1.5 rounded-md text-sm transition min-h-[36px] ${
                  filterStatus === option.value
                    ? "bg-purple-600 text-gray-900"
                    : "bg-slate-100 text-gray-700 hover:bg-slate-100"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>

          <div className="text-sm text-gray-500">
            Showing {sortedJobs.length} of {jobs.length} jobs
          </div>
        </div>

        {/* JOBS TABLE */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {/* Table Header */}
          <div className="grid grid-cols-[60px_100px_100px_minmax(0,1fr)_80px_120px_150px] gap-2 px-4 py-3 bg-slate-200 text-xs font-semibold text-gray-700 border-b border-gray-200">
            <button
              onClick={() => handleSort("status")}
              className="text-left hover:text-gray-900 transition flex items-center"
            >
              Status<SortIndicator column="status" />
            </button>
            <button
              onClick={() => handleSort("jobNumber")}
              className="text-left hover:text-gray-900 transition flex items-center"
            >
              Job #<SortIndicator column="jobNumber" />
            </button>
            <div>Product</div>
            <button
              onClick={() => handleSort("name")}
              className="text-left hover:text-gray-900 transition flex items-center"
            >
              Name<SortIndicator column="name" />
            </button>
            <button
              onClick={() => handleSort("qty")}
              className="text-center hover:text-gray-900 transition flex items-center justify-center"
            >
              Qty<SortIndicator column="qty" />
            </button>
            <div className="text-center">Operations</div>
            <div className="text-right">Actions</div>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-gray-200/50 max-h-[500px] overflow-y-auto">
            {sortedJobs.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-500">
                No jobs match the current filter.
              </div>
            ) : (
              sortedJobs.map((job) => {
                const statusBadge = getJobStatusBadge(job);
                const opProgress = getOperationProgress(job);
                const session = activeSessions[job.id];
                const isRunning = session?.state === "running";
                const isPaused = session?.state === "paused";
                const hasSession = isRunning || isPaused;

                return (
                  <div
                    key={job.id}
                    onClick={() => onJobClick?.(job)}
                    className="grid grid-cols-[60px_100px_100px_minmax(0,1fr)_80px_120px_150px] gap-2 px-4 py-3 hover:bg-slate-100 cursor-pointer transition min-h-[56px] items-center"
                  >
                    {/* Status */}
                    <div>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${statusBadge.color}`}>
                        {statusBadge.pulse && (
                          <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                        )}
                        {statusBadge.text}
                      </span>
                    </div>

                    {/* Job Number */}
                    <div className="font-semibold text-gray-900 text-sm">
                      {job.jobNumber}
                    </div>

                    {/* Product Code */}
                    <div className="text-xs text-gray-500 truncate" title={job.productCode}>
                      {job.productCode || "—"}
                    </div>

                    {/* Name */}
                    <div className="text-sm text-gray-800 truncate" title={job.name}>
                      {job.name || "Unnamed Job"}
                    </div>

                    {/* Quantity */}
                    <div className="text-center text-yellow-600 font-semibold text-sm">
                      {job.qty ?? "—"}
                    </div>

                    {/* Operations Progress */}
                    <div className="text-center">
                      {loadingRoutings ? (
                        <span className="text-xs text-gray-500">Loading...</span>
                      ) : opProgress ? (
                        <div className="flex flex-col items-center">
                          <span className="text-xs text-gray-700">
                            {opProgress.completed}/{opProgress.total}
                          </span>
                          <div className="w-full h-1 bg-gray-200 rounded-full mt-1">
                            <div
                              className="h-full bg-blue-500 rounded-full"
                              style={{ width: `${(opProgress.completed / opProgress.total) * 100}%` }}
                            />
                          </div>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-500">—</span>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                      {!hasSession && (
                        <button
                          onClick={() => onStartJob?.(job)}
                          disabled={!selectedOperation}
                          className={`px-3 py-1.5 rounded text-xs font-medium transition min-h-[32px] ${
                            selectedOperation
                              ? "bg-green-600 hover:bg-green-700 text-gray-900"
                              : "bg-gray-200 text-gray-500 cursor-not-allowed"
                          }`}
                          title={selectedOperation ? "Start job" : "Select an operation first"}
                        >
                          Start
                        </button>
                      )}

                      {isRunning && (
                        <>
                          <button
                            onClick={() => onPauseJob?.(job)}
                            className="px-2 py-1.5 bg-yellow-500 hover:bg-yellow-600 text-gray-900 text-xs font-medium rounded transition min-h-[32px]"
                          >
                            Pause
                          </button>
                          <button
                            onClick={() => onCompleteJob?.(job)}
                            className="px-2 py-1.5 bg-red-600 hover:bg-red-700 text-gray-900 text-xs font-medium rounded transition min-h-[32px]"
                          >
                            Done
                          </button>
                        </>
                      )}

                      {isPaused && (
                        <>
                          <button
                            onClick={() => onResumeJob?.(job)}
                            className="px-2 py-1.5 bg-green-600 hover:bg-green-700 text-gray-900 text-xs font-medium rounded transition min-h-[32px]"
                          >
                            Resume
                          </button>
                          <button
                            onClick={() => onCompleteJob?.(job)}
                            className="px-2 py-1.5 bg-red-600 hover:bg-red-700 text-gray-900 text-xs font-medium rounded transition min-h-[32px]"
                          >
                            Done
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* OPERATION SELECTOR HINT */}
        {!selectedOperation && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-500/30 rounded-lg text-center">
            <p className="text-blue-200 text-sm">
              Select an operation from the main screen to enable the Start button for jobs.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
